import React, { useRef, useEffect } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import InfoOverlayController from "./InfoOverlayController";
import {
  selectIsSpinning,
  selectSpinComplete,
  selectHoldAndWinActive,
  selectAutoPlayActive,
  selectBet,
  selectWinAnimationsPlaying,
  selectSymbolPopupActive,
} from "../../../State/selectors";
import { selectBaseBet } from "../../../State/Slices/gameSettingsSlice";
import { useTranslation } from "react-i18next";

/**
 * InfoOverlay - React wrapper component for InfoOverlayController
 * Manages controller lifecycle, dimensions updates, bet updates, and blocking conditions
 * Prevents popup interaction during spin, autoplay, Hold & Win, or win animations
 */
const InfoOverlay = () => {
  const { reelRefs, overlayReelRefs } = useReelRefs();
  const dimensions = useDimensions();
  const { t } = useTranslation();

  const isSpinning = useSelector(selectIsSpinning);
  const spinComplete = useSelector(selectSpinComplete);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const bet = useSelector(selectBet);
  const baseBet = useSelector(selectBaseBet);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);

  const overlayContainerRef = useRef(null);
  const controllerRef = useRef(null);
  const symbolPopupActive = useSelector(selectSymbolPopupActive);

  // Initialize controller
  useEffect(() => {
    if (overlayContainerRef.current && !controllerRef.current) {
      controllerRef.current = new InfoOverlayController(
        overlayContainerRef.current,
        dimensions,
        reelRefs,
        t,
        bet,
        overlayReelRefs
      );
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.destroy();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update dimensions on resize
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.resize(dimensions);
    }
  }, [dimensions]);

  // Update bet when it changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateBet(bet);
    }
  }, [bet]);

  // Update base bet when it changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateBaseBet(baseBet);
    }
  }, [baseBet]);

  // Close popup when blocking conditions become active
  useEffect(() => {
    if (!controllerRef.current) return;

    const hasActivePopup = controllerRef.current.hasActivePopup();

    // Close popup if it's active and any blocking condition is true
    if (
      hasActivePopup &&
      (isSpinning || holdAndWinActive || autoPlayActive || winAnimationsPlaying)
    ) {
      controllerRef.current.closePopup();
    }
  }, [isSpinning, holdAndWinActive, autoPlayActive, winAnimationsPlaying]);

  // Update interactive state based on all blocking conditions
  useEffect(() => {
    if (controllerRef.current) {
      // Only interactive if: not spinning, spin complete, not in holdAndWin, not in autoplay, not in win animation
      const interactive =
        !isSpinning &&
        spinComplete &&
        !holdAndWinActive &&
        !autoPlayActive &&
        !winAnimationsPlaying;

      controllerRef.current.setInteractive(interactive);
    }
  }, [
    isSpinning,
    spinComplete,
    holdAndWinActive,
    autoPlayActive,
    winAnimationsPlaying,
  ]);

  // Determine if container should be interactive
  const containerInteractive =
    !isSpinning &&
    spinComplete &&
    !holdAndWinActive &&
    !autoPlayActive &&
    !winAnimationsPlaying;

  // Tell controller to show/hide background click area
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.setBackgroundClickAreaActive(!!symbolPopupActive);
    }
  }, [symbolPopupActive]);

  return (
    <Container
      name="info_overlay"
      zIndex={10}
      eventMode={containerInteractive ? "static" : "none"}
      sortableChildren={true}
    >
      <Container
        ref={overlayContainerRef}
        name="info_overlay_container"
        sortableChildren
        zIndex={2}
      />
    </Container>
  );
};

export default InfoOverlay;
